"use client";

import { useState } from "react";
import Image from "next/image";
import type { ImageDataType } from "@/shared/types";
import { GalleryLightbox } from "@/shared/components/imageGallery/GalleryLightbox";
import { GalleryNavButton } from "@/shared/components/imageGallery/GalleryNavButton";
import { cn } from "@/lib/utils";

interface ProjectGalleryCarouselProps {
  images: ImageDataType[];
  className?: string;
}

const PROJECT_GALLERY_CONTROLS_THEME =
  "bg-background text-foreground hover:opacity-80";

/** Figma block_project — single-image carousel with lightbox like Gallery block. */
export function ProjectGalleryCarousel({
  images,
  className,
}: ProjectGalleryCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const hasMultipleImages = images.length > 1;
  const activeImage = images[activeIndex];

  if (!activeImage) {
    return null;
  }

  const goToPrevious = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === 0 ? images.length - 1 : currentIndex - 1
    );
  };

  const goToNext = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === images.length - 1 ? 0 : currentIndex + 1
    );
  };

  const openLightbox = () => {
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  return (
    <>
      <div className={cn("relative w-full", className)}>
        <button
          type="button"
          onClick={openLightbox}
          className="relative block aspect-[343/350] w-full cursor-pointer overflow-hidden md:aspect-[720/450] desktop:aspect-[880/500]"
          aria-label={`Открыть изображение ${activeIndex + 1}`}
        >
          <Image
            src={activeImage.src}
            alt={activeImage.alt || ""}
            title={activeImage.title ?? undefined}
            fill
            className="object-cover"
            sizes="(max-width: 767px) 100vw, (max-width: 1439px) 90vw, 880px"
            priority={activeIndex === 0}
          />
        </button>

        {hasMultipleImages && (
          <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-between px-4 md:px-6 desktop:px-10">
            <GalleryNavButton
              direction="prev"
              ariaLabel="Предыдущее фото"
              onClick={goToPrevious}
              themeClassName={PROJECT_GALLERY_CONTROLS_THEME}
              className="pointer-events-auto"
            />
            <GalleryNavButton
              direction="next"
              ariaLabel="Следующее фото"
              onClick={goToNext}
              themeClassName={PROJECT_GALLERY_CONTROLS_THEME}
              className="pointer-events-auto"
            />
          </div>
        )}
      </div>

      {isLightboxOpen && (
        <GalleryLightbox
          images={images}
          initialIndex={activeIndex}
          onClose={closeLightbox}
        />
      )}
    </>
  );
}
